import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import API_BASE_URL from "../config";
import { getUserId, getUserRole } from "../utils/Auth";
import { toast } from "react-toastify";
import { Loader2, PlusCircle, X, CheckCircle2, Calendar } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const CreateTaskModal = ({ isOpen, onClose, onTaskCreated }) => {
  const token = localStorage.getItem("token");
  const currentUserId = getUserId();
  const currentUserRole = getUserRole();
  const dateInputRef = useRef(null);
  const toastRef = useRef(null);
  const { darkMode } = useTheme();

  const defaultTask = {
    title: "",
    description: "",
    priority: 2,
    deadline: "",
    agentId: currentUserId,
  };

  const [newTask, setNewTask] = useState(defaultTask);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const showToastOnce = (type, message) => {
    if (toast.isActive(toastRef.current)) return;
    toastRef.current =
      type === "error" ? toast.error(message) : toast.success(message);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (Array.isArray(data)) setUsers(data);
      } catch {
        showToastOnce("error", "Eroare la încărcarea listei de utilizatori.");
      }
    };
    if (currentUserRole === "Admin") fetchUsers();
  }, [currentUserRole, token]);

  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => setNewTask(defaultTask), 200);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  // --- Keyboard shortcuts ---
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      // Ctrl + Enter => save
      if (e.ctrlKey && e.key === "Enter") {
        e.preventDefault();
        if (!loading) handleCreateTask();
      }
      // Esc => close
      if (e.key === "Escape") {
        e.preventDefault();
        if (!loading) onClose();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, newTask, loading]);

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      showToastOnce("error", "Titlul este obligatoriu!");
      return;
    }

    try {
      setLoading(true);
      const payload =
        currentUserRole === "Admin"
          ? {
            title: newTask.title,
            description: newTask.description,
            priority: newTask.priority,
            deadline: newTask.deadline || null,
            adminId: currentUserId,
            agentId: newTask.agentId,
          }
          : {
            title: newTask.title,
            description: newTask.description,
            priority: newTask.priority,
            deadline: newTask.deadline || null,
            agentId: currentUserId,
          };


      const res = await fetch(`${API_BASE_URL}/api/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        showToastOnce("error", data.message || "Eroare la crearea taskului.");
        return;
      }

      showToastOnce("success", "Task creat cu succes!");
      onTaskCreated(data.task);
      onClose();
      setNewTask(defaultTask);
    } catch {
      showToastOnce("error", "Eroare la server.");
    } finally {
      setLoading(false);
    }
  };

  return isOpen ? (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`p-8 rounded-3xl shadow-xl w-[700px] max-h-[90vh] overflow-y-auto relative transition-colors 
      ${darkMode ? "bg-gray-900 text-white border border-gray-700" : "bg-white text-gray-900 border border-gray-200"}`}
        onClick={(e) => e.stopPropagation()}
      >

        {/* HEADER */}
        <div
          className={`flex items-center justify-between mb-6 border-b pb-3 ${darkMode ? "border-gray-700" : "border-gray-200"
            }`}
        >
          <h3
            className={`text-2xl font-semibold flex items-center gap-2 ${darkMode ? "text-blue-400" : "text-blue-600"
              }`}
          >
            <PlusCircle className="w-6 h-6" />
            Creează un task nou
          </h3>
          <button
            onClick={onClose}
            className={`transition ${darkMode
              ? "text-red-400 hover:text-red-600"
              : "text-red-500 hover:text-red-700"
              }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>


        {/* BODY */}
        <div className="flex flex-col gap-6">
          {/* Title */}
          <div>
            <label
              className={`block text-sm mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"
                }`}
            >
              Titlu task:
            </label>
            <input
              type="text"
              placeholder="Ex: Contactează clientul..."
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              className={`w-full border rounded-lg px-3 py-2.5 outline-none transition focus:ring-2 focus:ring-blue-500 ${darkMode
                ? "bg-gray-800 border-gray-700 text-gray-100"
                : "bg-gray-50 border-gray-300 text-gray-900"
                }`}
            />

          </div>

          {/* Description */}
          <div>
            <label
              className={`block text-sm mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"
                }`}
            >
              Descriere (opțional):
            </label>
            <textarea
              placeholder="Detalii suplimentare despre task..."
              value={newTask.description}
              onChange={(e) =>
                setNewTask({ ...newTask, description: e.target.value })
              }
              className={`w-full border rounded-lg px-3 py-3 min-h-[140px] resize-y outline-none transition focus:ring-2 focus:ring-blue-500 ${darkMode
                ? "bg-gray-800 border-gray-700 text-gray-100"
                : "bg-gray-50 border-gray-300 text-gray-900"
                }`}
            />

          </div>

          {/* Priority */}
          <div>
            <label
              className={`block text-sm mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"
                }`}
            >
              Prioritate:
            </label>
            <select
              value={newTask.priority}
              onChange={(e) =>
                setNewTask({ ...newTask, priority: parseInt(e.target.value) })
              }
              className={`w-full border rounded-lg px-3 py-2.5 outline-none transition focus:ring-2 focus:ring-blue-500 ${darkMode
                ? "bg-gray-800 border-gray-700 text-gray-100"
                : "bg-gray-50 border-gray-300 text-gray-900"
                }`}
            >
              <option value={0}>🔥 Urgent</option>
              <option value={1}>⚠️ Mare</option>
              <option value={2}>🟡 Medie</option>
              <option value={3}>🟢 Mică</option>
            </select>

          </div>

          {/* Deadline */}
          <div>
            <label
              className={`block text-sm mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"
                }`}
            >
              Deadline:
            </label>
            <div
              onClick={() => dateInputRef.current?.showPicker()}
              className="relative flex items-center"
            >
              <input
                ref={dateInputRef}
                type="datetime-local"
                value={
                  newTask.deadline
                    ? new Date(
                      new Date(newTask.deadline).getTime() -
                      new Date().getTimezoneOffset() * 60000
                    )
                      .toISOString()
                      .slice(0, 16)
                    : ""
                }
                onChange={(e) => {
                  const localValue = e.target.value;
                  if (!localValue) {
                    setNewTask({ ...newTask, deadline: "" });
                    return;
                  }

                  // 🔹 Lokális → UTC (NINCS offset korrekció!)
                  const utcIso = new Date(localValue).toISOString();

                  setNewTask({ ...newTask, deadline: utcIso });
                }}
                className={`w-full border rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition cursor-pointer ${darkMode
                  ? "bg-gray-800 border-gray-700 text-gray-100"
                  : "bg-gray-50 border-gray-300 text-gray-900"
                  }`}
              />
            </div>
          </div>

          {/* Assign user */}
          {currentUserRole === "Admin" && (
            <div>
              <label
                className={`block text-sm mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
              >
                Atribuie către:
              </label>
              <select
                value={newTask.agentId}
                onChange={(e) =>
                  setNewTask({ ...newTask, agentId: parseInt(e.target.value) })
                }
                className={`w-full border rounded-lg px-3 py-2.5 outline-none transition focus:ring-2 focus:ring-blue-500 ${darkMode
                  ? "bg-gray-800 border-gray-700 text-gray-100"
                  : "bg-gray-50 border-gray-300 text-gray-900"
                  }`}
              >
                <option value={currentUserId}>— Eu (Admin) —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>

            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex justify-center gap-4 mt-10">
          <button
            onClick={onClose}
            disabled={loading}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition disabled:opacity-70 ${darkMode
              ? "bg-gray-700 hover:bg-gray-600 text-white"
              : "bg-gray-400 hover:bg-gray-500 text-white"
              }`}
          >
            <X className="w-4 h-4" />
            <span>Anulare</span>
            <span className="text-xs opacity-70">(Esc)</span>
          </button>

          <button
            onClick={handleCreateTask}
            disabled={loading}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition disabled:opacity-70 ${darkMode
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
          >

            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Salvare...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Salvează</span>
                <span className="text-xs opacity-70">(Ctrl + Enter)</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  ) : null;

};

export default CreateTaskModal;
